/**
 * SiteFooter — shared bottom footer. Same content as the original Landing
 * footer, extracted so subpages can reuse it without duplication.
 */
import { Link } from "react-router-dom";

export default function SiteFooter({ config }) {
  const settings = config?.settings || {};
  const discord = settings.discord_url || "#";
  const hasPortfolio = (config?.portfolio?.length ?? 0) > 0;

  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-[var(--theme-border)] bg-black/60 backdrop-blur-sm pt-16 pb-8 px-6 md:px-10 overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[640px] h-64 rounded-full bg-[var(--theme-accent-primary)]/[0.04] blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto grid gap-12 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <img src="/logo.png?v=4" alt="Null Modifications logo" className="h-9 w-9 object-contain" />
            <span className="font-display text-2xl tracking-[0.08em] uppercase">
              Null Modifications
            </span>
          </div>
          <p className="text-sm text-[var(--muted)] leading-relaxed max-w-sm">
            Underground 3D vehicle modeling. Custom builds, server exclusives, and one-of-one commissions for serious RP servers.
          </p>
        </div>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--theme-text-muted)] mb-4">Navigate</div>
          <div className="flex flex-col gap-2.5">
            <Link to="/pricing" data-testid="footer-nav-pricing" className="text-sm text-[var(--muted)] hover:text-[var(--accent)] transition-colors w-fit">Pricing</Link>
            <Link to="/products" data-testid="footer-nav-products" className="text-sm text-[var(--muted)] hover:text-[var(--accent)] transition-colors w-fit">Shop</Link>
            {hasPortfolio && (
              <Link to="/portfolio" data-testid="footer-nav-portfolio" className="text-sm text-[var(--muted)] hover:text-[var(--accent)] transition-colors w-fit">Portfolio</Link>
            )}
            <Link to="/tos" data-testid="footer-nav-tos" className="text-sm text-[var(--muted)] hover:text-[var(--accent)] transition-colors w-fit">Terms of Service</Link>
          </div>
        </div>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--theme-text-muted)] mb-4">Contact</div>
          <a
            data-testid="footer-discord"
            href={discord}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-[var(--accent)] hover:underline break-all block"
          >
            {discord}
          </a>
          {settings.support_email && (
            <a
              data-testid="footer-support-email"
              href={`mailto:${settings.support_email}`}
              className="text-sm text-[var(--accent)] hover:underline break-all block mt-2.5"
            >
              {settings.support_email}
            </a>
          )}
        </div>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--theme-text-muted)] mb-4">System</div>
          <Link
            to="/admin"
            data-testid="footer-admin-link"
            className="text-sm text-[var(--muted)] hover:text-[var(--accent)] transition-colors"
          >
            Admin Portal
          </Link>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto mt-14 pt-6 border-t border-[var(--theme-border)] flex flex-col md:flex-row justify-between gap-4 font-mono text-[10px] text-[var(--muted)] uppercase tracking-[0.24em]">
        <div>© NULL MODIFICATIONS — ALL RIGHTS RESERVED</div>
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          BUILD_{new Date().getFullYear()}.{(new Date().getMonth() + 1).toString().padStart(2, "0")} — ONLINE
        </div>
      </div>
    </footer>
  );
}
